/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { changeStyleWithHosting } from "utility/hosting";

import type { ChangeEventHandler, FC } from "react";

type Props = {
  checked: boolean;
  description?: string;
  label: string;
  name: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  value: string;
};

export const RadioButtonWithLabel: FC<Props> = ({
  checked,
  description,
  label,
  name,
  onChange,
  value,
}) => {
  const classes = styles();

  return (
    <label css={[classes.root, checked && classes.checked]}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        css={classes.input}
      />
      <span css={classes.indicator} aria-hidden="true" />
      <span css={classes.content}>
        <span css={classes.label}>{label}</span>
        {description && <span css={classes.description}>{description}</span>}
      </span>
    </label>
  );
};

const styles = () => {
  const { accentColor } = changeStyleWithHosting().baseStyles;

  return {
    root: css`
      position: relative;
      display: grid;
      grid-template-columns: 20px minmax(0, 1fr);
      align-items: center;
      gap: 24px;
      min-height: 52px;
      padding: 12px 16px;
      background-color: #fff;
      border: 1px solid #aaa;
      cursor: pointer;
    `,
    checked: css`
      background-color: #e6fffa;
      border: 2px solid ${accentColor};
    `,
    input: css`
      position: absolute;
      width: 1px;
      height: 1px;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
    `,
    indicator: css`
      position: relative;
      width: 20px;
      height: 20px;
      border: 1px solid #aaa;
      border-radius: 50%;
      input:checked + & {
        border-color: ${accentColor};
      }
      input:focus-visible + & {
        outline: 2px solid ${accentColor};
        outline-offset: 2px;
      }
      input:checked + &::after {
        content: "";
        position: absolute;
        top: 3px;
        left: 3px;
        width: 12px;
        height: 12px;
        background-color: ${accentColor};
        border-radius: 50%;
      }
    `,
    content: css`
      display: grid;
      gap: 2px;
      min-width: 0;
    `,
    label: css`
      color: #222;
      font-size: 14px;
      font-weight: bold;
      line-height: 1.5;
    `,
    description: css`
      color: #777;
      font-size: 12px;
      line-height: 1.5;
    `,
  };
};
